import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import { GridRenderCellParams, useGridApiRef } from "@mui/x-data-grid-pro";

import { ListEventsRequest } from "shared/api/api";
import { getClaimsExport } from "shared/api/claims/api";
import { useListClaims, useListClaimsCount } from "shared/api/claims/hooks";
import { getSortFilter } from "shared/api/utils";
import { MAX_ROWS_DOWNLOAD_LIMIT } from "shared/constants";
import { useTenantMileageUnit } from "shared/hooks";
import { useClaimsSchema } from "shared/schemas/claimsSchema";
import { DataType, EventTypeEnum } from "shared/types";

import {
  CLAIMS_TABLE_DEFAULT_SORT,
  CLAIMS_TABLE_PAGE_KEY,
} from "pages/ClaimAnalytics/constants";
import { DEFAULT_CLAIMS_FIELDS } from "pages/Dashboard/components/constants";
import ClaimDetailsDrawer from "pages/Dashboard/components/EventDetails/ClaimDetails/ClaimDetailsDrawer";
import EventsTableToolbar from "pages/Dashboard/components/EventsTableToolbar";
import VINLink from "pages/Dashboard/components/VINLink";
import { TableComponentProps } from "pages/Dashboard/types";
import { ROWS_PER_PAGE } from "pages/shared/Dashboard/constants";
import { LandingPageViewConfig } from "pages/shared/Dashboard/types";
import DetailsLinkForEventType from "pages/VINView/ServiceRecords/EventDetail/DetailsLinkForEventType";

import APIError from "features/ui/APIError";
import { FilterGroupState } from "features/ui/Filters/FilterBuilder/types";
import {
  getFiltersQuery,
  mergeFilterGroupStates,
} from "features/ui/Filters/FilterBuilder/utils";
import { useFilterSortState } from "features/ui/Filters/hooks";
import { FilterType } from "features/ui/Filters/types";
import MaterialTable, { SchemaEntry } from "features/ui/MaterialTable";

import OpenDetailsButton from "./OpenDetailsButton";

const getRelatedTypes = (config: LandingPageViewConfig) =>
  config.eventDetails?.relatedEvents
    .filter((group) => group.eventType === EventTypeEnum.SIGNAL_EVENT)
    .map((group) => ({
      title: group.title,
      signalEventType: group.signalEventType,
    }));

const formatClaimListCellFunc =
  (renderCellParams: any) => (params: GridRenderCellParams) => {
    const { ID, VIN, matchedEventCounts, matchedClaimsCount } = params.row;
    const { t } = renderCellParams;
    const relatedTypes = getRelatedTypes(renderCellParams.config);

    const matched = relatedTypes?.map(
      (type) => `matchedEventCounts.${type.signalEventType}`
    );

    if (matched?.includes(params.field)) {
      const parts = params.field.split(".");
      if (parts.length > 1) {
        const count = matchedEventCounts?.[parts[1]];

        return <OpenDetailsButton>{count ?? 0}</OpenDetailsButton>;
      }
    }

    switch (params.field) {
      case "VIN":
        return <VINLink config={renderCellParams.config} vin={VIN} />;
      case "photos":
        return params.row.photos?.length ? (
          <OpenDetailsButton className="!flex gap-2 justify-center items-center">
            {params.row.photos?.length}
            <PhotoCameraOutlinedIcon fontSize="small" />
          </OpenDetailsButton>
        ) : (
          <div className="flex justify-center">{t("common:not_available")}</div>
        );
      case "vehiclePhotos":
        return params.row.vehiclePhotos?.length ? (
          <OpenDetailsButton className="!flex gap-2 justify-center items-center">
            {params.row.vehiclePhotos?.length}
            <PhotoCameraOutlinedIcon fontSize="small" />
          </OpenDetailsButton>
        ) : (
          <div className="flex justify-center">{t("common:not_available")}</div>
        );
      case "attachments":
        return params.row.attachments?.length ? (
          <OpenDetailsButton className="!flex gap-2 justify-center items-center">
            {params.row.attachments?.length}
            <FileCopyOutlinedIcon fontSize="small" />
          </OpenDetailsButton>
        ) : (
          <div className="flex justify-center">{t("common:not_available")}</div>
        );
      case "vehicleAttachments":
        return params.row.vehicleAttachments?.length ? (
          <OpenDetailsButton className="!flex gap-2 justify-center items-center">
            {params.row.vehicleAttachments?.length}
            <FileCopyOutlinedIcon fontSize="small" />
          </OpenDetailsButton>
        ) : (
          <div className="flex justify-center">{t("common:not_available")}</div>
        );

      case "ID":
        return (
          <div className="max-w-30 overflow-hidden text-ellipsis" title={ID}>
            <DetailsLinkForEventType type={EventTypeEnum.CLAIM} ID={ID} />
          </div>
        );
      case "matchedClaimsCount":
        return <OpenDetailsButton>{matchedClaimsCount ?? 0}</OpenDetailsButton>;
    }
  };

const createMatchedCountsSchema = (
  config: LandingPageViewConfig
): SchemaEntry[] | undefined => {
  const relatedTypes = getRelatedTypes(config);

  return relatedTypes?.map((type): SchemaEntry => ({
    label: type.title,
    accessor: `matchedEventCounts.${type.signalEventType}`,
    dataType: DataType.NUMBER,
    sortable: true,
    sort: {
      fieldNameForAPI: `matchedEventCounts.${type.signalEventType}`,
    },
    filter: {
      filterType: FilterType.NUMBER,
      disableSelectFilters: true,
      onlyAllowPositiveIntegers: true,
      fieldNameForAPI: `matchedEventCounts.${type.signalEventType}`,
      label: type.title,
      fieldName: `matchedEventCounts.${type.signalEventType}`,
      isCount: true,
    },
  }));
};

const ClaimsTable = ({
  eventFilters,
  vehiclesFilters,
  onBadRequest,
  config,
  showCount = false,
  pageKey = CLAIMS_TABLE_PAGE_KEY,
  limit = ROWS_PER_PAGE,
  hideFooter = false,
  hideExport = false,
  onDataChange,
  smartGroupId,
  smartGroups,
  onSmartGroupsChange,
  dashboardName,
  showUngroupedEvents,
  setShowUngroupedEvents,
  groupedCount,
  ungroupedCount,
  disablePersistence,
  sharedTableFilterState,
  customActions,
  downloadSource,
  isDrawerOpen,
  onDrawerClose,
  onFiltersChange,
  defaultFilterValues,
  rowTestId,
  stickyHeader,
  stickyFooter,
  stickyTopOffset,
}: TableComponentProps) => {
  const { t } = useTranslation();
  const [drawerIndex, setDrawerIndex] = useState<number | null>(null);
  const apiRef = useGridApiRef();
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  const [pageIndex, setPageIndex] = useState<number>(0);
  const [claimIndex, setClaimIndex] = useState<number>(0);

  const { schema, attributeAccessors } = useClaimsSchema(
    [],
    config.columnAccessors || DEFAULT_CLAIMS_FIELDS
  );

  const matchedEventCountsSchema = createMatchedCountsSchema(config);

  const schemaNoWideColumns = schema.map((entry) => ({
    ...entry,
    limitedWidthClass: undefined,
  }));

  const tableSchema = [
    ...schemaNoWideColumns,
    ...(matchedEventCountsSchema || []),
  ];

  // use a shared table filter state if provided, otherwise create a local filter state
  const localFilterState = useFilterSortState({
    pageKey,
    defaultSort: CLAIMS_TABLE_DEFAULT_SORT,
    schemaAttributes: attributeAccessors,
    disableUsingQuery: disablePersistence,
    disableUsingLocalStorage: disablePersistence,
    defaultFilterValues,
  });

  const filterSortState = sharedTableFilterState || localFilterState;

  const {
    resetFilters,
    filters,
    initialized: filtersInitialized,
    sort,
    manageOnSortChange,
    resetFilterSortState,
    updateFilters,
  } = filterSortState;

  useEffect(() => {
    if (defaultFilterValues) {
      updateFilters(defaultFilterValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultFilterValues]);

  const handleUpdateFilters = (updatedFilters: FilterGroupState) => {
    updateFilters(updatedFilters);
    if (onFiltersChange) {
      onFiltersChange(updatedFilters);
    }
  };

  const requestParams: ListEventsRequest = {
    sort: getSortFilter(sort),
    filter: getFiltersQuery(mergeFilterGroupStates(filters, eventFilters)),
    vehiclesFilter: vehiclesFilters && getFiltersQuery(vehiclesFilters),
    limit,
    mileageUnit: useTenantMileageUnit(),
    analytics: true,
    returnVehicles: true,
    returnMatchedEventCounts: true,
    returnPhotos: true,
    returnSmartGroups: true,
    returnAttachments: true,
    includeSrcURLs: true,
  };

  const { data, isLoading, error, ...paginationData } =
    useListClaims(requestParams);

  const handleClose = () => {
    setDrawerIndex(null);
    onDrawerClose && onDrawerClose();
  };

  const onNext = () => {
    if (drawerIndex == null) return;

    if (limit === drawerIndex + 1 && paginationData.hasNextPage) {
      paginationData.onNextPage();
      setDrawerIndex(0);
      setPageIndex((prev) => prev + 1);
    } else {
      setDrawerIndex(drawerIndex + 1);
    }

    setClaimIndex((prev) => prev + 1);
  };

  const onPrevious = () => {
    if (drawerIndex == null) return;

    if (drawerIndex === 0 && paginationData.hasPreviousPage) {
      paginationData.onPreviousPage();
      setDrawerIndex(limit - 1);
      setPageIndex((prev) => prev - 1);
    } else {
      setDrawerIndex(drawerIndex - 1);
    }

    setClaimIndex((prev) => prev - 1);
  };

  const downloadDisabled = !data || data.length === 0;

  const { data: countData } = useListClaimsCount({
    filter: requestParams.filter,
    vehiclesFilter: requestParams.vehiclesFilter,
    analytics: true,
  });

  const onRowClick = (index: number) => {
    setDrawerIndex(index);
    setClaimIndex(ROWS_PER_PAGE * (pageIndex ?? 0) + index + 1);
  };

  const handleFilterClick = () => {
    if (isFilterPanelOpen) {
      apiRef.current?.hideFilterPanel();
    } else {
      apiRef.current?.showFilterPanel();
    }
  };

  const activeFilterCount = filters
    ? Object.keys(filters.children || {}).length
    : 0;

  useEffect(() => {
    if (onDataChange && data) {
      onDataChange(data);
    }
  }, [data, onDataChange]);

  useEffect(() => {
    const selectedClaim = data?.find((_, i) => i === drawerIndex);
    if (!selectedClaim) {
      if (isDrawerOpen) {
        setDrawerIndex(0);
        setClaimIndex(1);
      } else {
        onDrawerClose && onDrawerClose();
      }
    }
  }, [data, drawerIndex, onDrawerClose, isDrawerOpen]);

  if (error) {
    return (
      <APIError
        error={error}
        onBadRequest={() => {
          resetFilterSortState();
          onBadRequest();
        }}
      />
    );
  }

  const toolbar = () => (
    <EventsTableToolbar
      smartGroups={smartGroups}
      onSmartGroupsChange={onSmartGroupsChange}
      groupedCount={groupedCount}
      ungroupedCount={ungroupedCount}
      showUngroupedEvents={showUngroupedEvents}
      setShowUngroupedEvents={setShowUngroupedEvents}
      smartGroupsAllowed={config.enableSmartGroups ?? false}
      dashboardName={dashboardName}
      isLoading={isLoading}
      showCount={showCount}
      countData={countData}
      customActions={customActions}
      eventType={EventTypeEnum.CLAIM}
      filters={{
        activeFilterCount,
        onClick: handleFilterClick,
        dataTestId: "claims-table-filter-button",
      }}
      download={
        !hideExport
          ? {
              disabled: downloadDisabled,
              downloadFunc: getClaimsExport,
              downloadSource,
              fileName: "claims",
              requestParams: {
                ...requestParams,
                limit: MAX_ROWS_DOWNLOAD_LIMIT,
              },
              count: countData?.count as number,
              entityLabel: {
                key: `common:event_type.${EventTypeEnum.CLAIM.toLowerCase()}`,
              },
            }
          : undefined
      }
    />
  );

  return (
    <div>
      <MaterialTable
        schema={tableSchema}
        data={data}
        {...paginationData}
        pageSize={limit}
        page={pageIndex}
        rowCount={countData?.count || 0}
        sort={sort}
        handleSorting={manageOnSortChange}
        formatCell={formatClaimListCellFunc({
          claimFilter: eventFilters,
          onRowClick,
          config,
          t,
        })}
        loading={isLoading}
        filtersInitialized={filtersInitialized}
        onFiltersReset={resetFilters}
        filters={filters}
        updateFilters={handleUpdateFilters}
        hideFooter={hideFooter}
        onPageChange={(page) => setPageIndex(page)}
        toolbar={toolbar}
        apiRef={apiRef}
        onPreferencePanelOpen={() => setIsFilterPanelOpen(true)}
        onPreferencePanelClose={() => setIsFilterPanelOpen(false)}
        pageKey={pageKey}
        onRowClick={onRowClick}
        ignoreRowClickOnCells={["VIN"]}
        rowTestId={rowTestId}
        stickyHeader={stickyHeader}
        stickyFooter={stickyFooter}
        stickyTopOffset={stickyTopOffset}
      />
      <ClaimDetailsDrawer
        claim={data?.find((_, i) => i === drawerIndex)}
        count={countData?.count ?? 0}
        claimIndex={claimIndex}
        onNext={onNext}
        onPrevious={onPrevious}
        openedIndex={drawerIndex}
        onClose={handleClose}
        config={config}
        smartGroupId={smartGroupId}
        isLoading={isLoading}
      />
    </div>
  );
};

export default ClaimsTable;
